import axios, { AxiosResponse } from 'axios';

/**
 * Base API URL configuration
 */
const getApiBaseUrl = () => {
	const apiUrl = process.env.VITE_API_URL || import.meta.env.VITE_API_URL;

	if (import.meta.env.DEV) {
		return '/api/v1';
	}

	return `${apiUrl}/api/v1` || 'http://localhost:3000/api/v1';
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Get authentication headers
 */
const getAuthHeaders = (): Record<string, string> => {
	const token = sessionStorage.getItem('auth_token');
	return {
		'Content-Type': 'application/json',
		...(token && { Authorization: `Bearer ${token}` }),
	};
};

/**
 * User role enumeration
 */
export enum UserRole {
	VIEWER = 'VIEWER',
	READ = 'READ',
	EXECUTE = 'EXECUTE',
	WRITE = 'WRITE',
	SUPPORT = 'SUPPORT',
	ADMIN = 'ADMIN',
}

/**
 * Admin user interface
 */
export interface AdminUser {
	id: string;
	email: string;
	name: string;
	picture?: string;
	roles: UserRole[];
	isActive: boolean;
	lastLoginAt?: Date;
	createdAt: Date;
}

/**
 * Update user roles request
 */
export interface UpdateUserRolesRequest {
	roles: UserRole[];
	reason?: string;
}

/**
 * Bulk user operation request
 */
export interface BulkUserOperationRequest {
	userIds: string[];
	role?: UserRole;
	reason?: string;
}

/**
 * Admin users list response
 */
export interface AdminUsersListResponse {
	users: AdminUser[];
	total: number;
	page: number;
	limit: number;
	pages: number;
}

/**
 * User statistics response
 */
export interface UserStatisticsResponse {
	total: number;
	active: number;
	inactive: number;
	roleDistribution: Record<string, number>;
	recentLogins: Array<{
		email: string;
		name: string;
		lastLoginAt: Date;
	}>;
}

/**
 * Audit log entry
 */
export interface AuditLogEntry {
	id: string;
	actorEmail: string;
	actorName?: string;
	targetEmail: string;
	targetName?: string;
	action: string;
	beforeData?: any;
	afterData?: any;
	description?: string;
	metadata?: {
		reason?: string;
		riskLevel?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
	};
	createdAt: Date;
}

/**
 * Admin API client for user management
 */
class AdminApiClient {
	private readonly timeout: number = 30000;

	/**
	 * Handle API errors consistently
	 */
	private handleError(error: any): never {
		if (axios.isAxiosError(error)) {
			const errorMessage = error.response?.data?.message || error.message;
			throw new Error(errorMessage);
		}
		throw new Error(error.message || 'Unknown error occurred');
	}

	/**
	 * Get all users with optional filtering and pagination
	 */
	async getUsers(
		params: {
			page?: number;
			limit?: number;
			search?: string;
			role?: UserRole;
			isActive?: boolean;
		} = {}
	): Promise<AdminUsersListResponse> {
		try {
			const response: AxiosResponse<AdminUsersListResponse> = await axios.get(
				`${API_BASE_URL}/admin/users`,
				{
					headers: getAuthHeaders(),
					timeout: this.timeout,
					params,
				}
			);

			return response.data;
		} catch (error) {
			this.handleError(error);
		}
	}

	/**
	 * Get user details by ID
	 */
	async getUserById(userId: string): Promise<
		AdminUser & {
			recentActivity: any[];
			roleHistory: any[];
		}
	> {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/users/${userId}`, {
				headers: getAuthHeaders(),
				timeout: this.timeout,
			});

			return response.data;
		} catch (error) {
			this.handleError(error);
		}
	}

	/**
	 * Update user roles
	 */
	async updateUserRoles(userId: string, data: UpdateUserRolesRequest): Promise<AdminUser> {
		try {
			const response: AxiosResponse<AdminUser> = await axios.patch(
				`${API_BASE_URL}/admin/users/${userId}/roles`,
				data,
				{
					headers: getAuthHeaders(),
					timeout: this.timeout,
				}
			);

			return response.data;
		} catch (error) {
			this.handleError(error);
		}
	}

	/**
	 * Deactivate user account
	 */
	async deactivateUser(userId: string, reason?: string): Promise<AdminUser> {
		try {
			const response: AxiosResponse<AdminUser> = await axios.patch(
				`${API_BASE_URL}/admin/users/${userId}/deactivate`,
				{ reason },
				{
					headers: getAuthHeaders(),
					timeout: this.timeout,
				}
			);

			return response.data;
		} catch (error) {
			this.handleError(error);
		}
	}

	/**
	 * Bulk assign roles to multiple users
	 */
	async bulkAssignRoles(data: BulkUserOperationRequest): Promise<{
		success: number;
		failed: number;
		errors: Array<{ userId: string; error: string }>;
	}> {
		try {
			const response = await axios.post(`${API_BASE_URL}/admin/users/bulk/roles`, data, {
				headers: getAuthHeaders(),
				timeout: this.timeout,
			});

			return response.data;
		} catch (error) {
			this.handleError(error);
		}
	}

	/**
	 * Get audit logs for user management actions
	 */
	async getAuditLogs(
		params: {
			page?: number;
			limit?: number;
			userId?: string;
			action?: string;
			dateFrom?: string;
			dateTo?: string;
		} = {}
	): Promise<{
		logs: AuditLogEntry[];
		total: number;
		page: number;
		limit: number;
	}> {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/users/audit/logs`, {
				headers: getAuthHeaders(),
				timeout: this.timeout,
				params,
			});

			return response.data;
		} catch (error) {
			this.handleError(error);
		}
	}

	/**
	 * Get user management statistics
	 */
	async getStatistics(): Promise<UserStatisticsResponse> {
		try {
			const response: AxiosResponse<UserStatisticsResponse> = await axios.get(
				`${API_BASE_URL}/admin/users/statistics`,
				{
					headers: getAuthHeaders(),
					timeout: this.timeout,
				}
			);

			return response.data;
		} catch (error) {
			this.handleError(error);
		}
	}

	/**
	 * Invite a new user
	 */
	async inviteUser(invitation: {
		email: string;
		name: string;
		role: string;
		message?: string;
	}): Promise<{ success: boolean; message: string; invitationId?: string }> {
		try {
			const response = await axios.post(`${API_BASE_URL}/admin/users/invite`, invitation, {
				headers: getAuthHeaders(),
				timeout: this.timeout,
			});

			return response.data;
		} catch (error) {
			this.handleError(error);
		}
	}

	/**
	 * Get security events for dashboard
	 */
	async getSecurityEvents(
		params: {
			timeRange?: string;
			eventType?: string;
		} = {}
	): Promise<{
		events: any[];
		metrics: {
			totalUsers: number;
			activeUsers: number;
			failedLogins24h: number;
			roleChanges7d: number;
			securityEvents30d: number;
			threatLevel: string;
		};
	}> {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/users/security/events`, {
				headers: getAuthHeaders(),
				timeout: this.timeout,
				params,
			});

			return response.data;
		} catch (error) {
			this.handleError(error);
		}
	}

	/**
	 * Get comprehensive audit logs
	 */
	async getAuditLogsDetailed(
		params: {
			page?: number;
			limit?: number;
			action?: string;
			riskLevel?: string;
			dateRange?: string;
		} = {}
	): Promise<{
		logs: any[];
		total: number;
		page: number;
		limit: number;
	}> {
		try {
			const response = await axios.get(`${API_BASE_URL}/admin/users/audit/logs`, {
				headers: getAuthHeaders(),
				timeout: this.timeout,
				params,
			});

			return response.data;
		} catch (error) {
			this.handleError(error);
		}
	}
}

/**
 * Singleton admin API client instance
 */
export const adminApi = new AdminApiClient();

/**
 * React Query keys for admin API
 */
export const adminQueryKeys = {
	users: ['admin', 'users'] as const,
	usersList: (params: any) => ['admin', 'users', 'list', params] as const,
	userDetails: (id: string) => ['admin', 'users', 'details', id] as const,
	auditLogs: (params: any) => ['admin', 'audit', 'logs', params] as const,
	statistics: ['admin', 'statistics'] as const,
};
